import { UIIcon } from '../../config/icons';
import { EPayoutDestinationKind } from '../mdl/e-payout';
import { UIInputAdapter } from '../ui/component/adapter';
import { UIButton } from '../ui/component/button';
import { UIChipInput } from '../ui/component/chip';
import { UIImageInput } from '../ui/component/image';
import { UINumberInput, UITextInput, UITextInputOptions } from '../ui/component/input';
import { UIList } from '../ui/component/list';
import { $el } from '../ui/elements';
import { uiStartOrder } from '../ui/order';
import { onInteract } from '../ui/touch';
import { UIClassesSpec } from '../ui/types';
import { UIView } from '../ui/view';
import { YMultiCurrencyAmount } from '../y/amount';
import { $on, YEventHandlers, YInputEvent, YStateEvent, YValueEvent } from '../y/events';
import { $form, YForm, YInput } from '../y/form';
import { yEqual } from '../y/object';
import { YId } from '../y/types';

export class ABObjectCount extends UIView {
    private _count: HTMLElement;

    constructor(name: string) {
        super('div', 'ab-object-count');

        this.ui_add([this._count = $el.SPAN('count'), $el.SPAN('name', name)]);
    }

    set number(count: number) {
        this._count.textContent = count + ' ';
    }

    set text(text: string) {
        this._count.textContent = text + ' ';
    }
}

export class ABInputSuggestions extends UIView {
    private _input: UIChipInput;
    private _suggestions: string[];

    constructor(input: UIChipInput) {
        super('div', 'tl-input-suggestions ui-input-helper');

        this._input = input;
        this._suggestions = [];
        this._fill();
    }

    private _fill() {
        this.ui_empty();

        if(this._suggestions.length) {
            this.ui_add($el.I('Suggested:\u00a0\u00a0'));
            for(const s of this._suggestions) {
                const button = new UIButton({
                    kind: 'k-text', classes: 'm-inline', title: s, on_click: () => {
                        this._input.push(s);
                        button.remove();
                    }
                });
                this.ui_add(button);
            }
        }
    }

    set suggestions(s: string[]) {
        if(yEqual(s, this._suggestions)) return;
        this._suggestions = s;
        this._fill();
    }
}

export class ABInputArray extends UIList {
    adapter: UIInputAdapter<string>
    add_button: UIButton;

    constructor(add_title: string, input_generator: () => (UIView & YInput<any>) = () => new UITextInput, new_value: string = '') {
        const adapter = new UIInputAdapter((_s: string, entry) => {
            const input = input_generator();
            const container = $el.DIV('l-input-row', [input, new UIButton({ icon: UIIcon.TRASH_CAN, on_click: () => { adapter.remove(entry); } })]);
            return { view: container, input: input };
        });

        super(adapter, { classes: 'l-form' });

        this.adapter = adapter;
        // TODO: setting focus to true causes UISelects to scroll to the top... fix that and enable again!
        this.add_button = new UIButton({ title: add_title, kind: 'k-text', on_click: () => { adapter.push(new_value) } });
    }
}

export class ABMultiCurrencyInput extends UIView implements YInput<YMultiCurrencyAmount> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<YMultiCurrencyAmount>>;

    private _form: YForm<YMultiCurrencyAmount>

    constructor(scale: number) {
        super('div', 'tl-multi-currency-input i-input');

        const sek = new UINumberInput({ scale: scale, l_deco: $el.I('SEK') });
        const eur = new UINumberInput({ scale: scale, decimal: true, l_deco: $el.I('EUR') });

        this._form = $form({ SEK: sek, EUR: eur });

        this.ui_add([sek, eur]);
    }

    get value(): YMultiCurrencyAmount {
        return this._form.value;
    }

    set value(v: YMultiCurrencyAmount) {
        this._form.value = v;
    }

    get okay(): boolean {
        return this._form.okay;
    }

    get dirty(): boolean {
        return this._form.dirty;
    }

    set dirty(d: boolean) {
        this._form.dirty = d;
    }

    get disabled(): boolean {
        return this._form.disabled;
    }

    set disabled(d: boolean) {
        this._form.disabled = d;
    }
}

export class ABPicturesArray extends UIList // TODO: maybe issue
{
    adapter: UIInputAdapter<YId | undefined>
    add_button: UIButton;

    constructor(add_title: string, scope: string, hide_button?: boolean, download?: boolean) {
        const adapter = new UIInputAdapter<YId | undefined>((_, entry) => {
            const picture = new UIImageInput({ scope: scope, required: true, always_remove: true, download: download });
            picture.on_remove = () => {
                if(adapter.count > 1)
                    adapter.remove(entry);
            }
            if(hide_button)
                $on(picture, 'value', v => { v && this.add_button.ui_show() });
            onInteract(picture.buttons_view, e => uiStartOrder(e, this.onOrder(picture)));
            return { view: picture, input: picture };
        });

        super(adapter, { classes: 'tl-pictures-array' });

        this.adapter = adapter;
        this.add_button = new UIButton({ title: add_title, kind: 'k-text', on_click: () => { adapter.push(undefined) } });

        if(hide_button)
            this.add_button.ui_hide();
    }
}

export class ABNewPicturesArray extends UIList // TODO: maybe issue
{
    adapter: UIInputAdapter<YId | undefined>
    add_button: UIButton;
    _credits: boolean;

    constructor(add_title: string, scope: string, hide_button?: boolean, download?: boolean, classes?: UIClassesSpec, credits?: boolean) {
        const adapter = new UIInputAdapter<YId | undefined>((_, entry) => {
            const picture = new UIImageInput({ scope: scope, required: true, always_remove: true, download: download });
            picture.on_remove = () => {
                if(adapter.count > 1)
                    adapter.remove(entry);
            }
            if(hide_button)
                $on(picture, 'value', v => { v && this.add_button.ui_show() });
            const content = $el.DIV('r-item', picture);
            if(this._credits) {
                content.ui_add(new UITextInput({ kind: 'k-subtle', placeholder: 'Photo credits', classes: 't-sz-small' }));
            }
            onInteract(picture.buttons_view, e => uiStartOrder(e, this.onOrder(content)));
            return { view: content, input: picture };
        });

        super(adapter, { classes: ['tl-new-pictures-array', classes] });

        this._credits = !!credits;
        this.adapter = adapter;
        this.add_button = new UIButton({ title: add_title, kind: 'k-text', classes: 'i-input', on_click: () => { adapter.push(undefined) } });

        if(hide_button)
            this.add_button.ui_hide();
    }

    set credits(c: boolean) {
        if(this._credits === c) return;
        this._credits = c;
        this.adapter.updateAll();
    }

    get credits(): boolean {
        return this._credits;
    }
}

export const ab_transfer_destination_kind_values: { key: EPayoutDestinationKind, title: string }[] = [
    { key: 'bankgiro', title: 'Bankgiro' },
    { key: 'plusgiro', title: 'Plusgiro' },
    { key: 'bankaccount', title: 'Bankkonto' },
    { key: 'iban', title: 'IBAN' },
];

const destination_number_regexes: { [key in EPayoutDestinationKind]: RegExp } = {
    bankgiro: /^\d{3,4}-\d{4}$/,
    plusgiro: /^\d{4,7}-\d$/,
    bankaccount: /^\d{4}-?\d{6,12}$/,
    iban: /^[A-Z]{2}[ ]?[0-9]{2}(?:[ ]?[0-9A-Z]){10,30}$/
};

const default_destination_fixer = /[^0-9,.* +()-]/g;

const destination_fixer_regexes: { [key in EPayoutDestinationKind]: RegExp } = {
    bankgiro: default_destination_fixer,
    plusgiro: default_destination_fixer,
    bankaccount: default_destination_fixer,
    iban: /[^A-Z0-9,.* +()-]/g
};


export function makeTransferDestinationInput(kind: YInput<EPayoutDestinationKind | undefined>, options: UITextInputOptions = {}) {
    const input = new UITextInput({
        validator: s => {
            const k = kind.value;
            return !s || !k || destination_number_regexes[k].test(s);
        },
        fixer: s => {
            const k = kind.value;
            return [s.replace((k && destination_fixer_regexes[k]) || default_destination_fixer, ''), undefined]
        },
        invalid_message: 'Invalid account number',
        ...options
    });

    $on(kind, 'value', () => {
        input.check();
    });

    return input;
}
